import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getEmployeesByAdminAndOrg } from "services/getEmployees";
import {
  updateInternalLegalResearchRequest,
  updateLegalResearchRequest,
  updateLegalResearchRequestCommittee,
} from "services/legalResearchService";
import {
  createProsecutorAssignment,
  deleteProsecutorAssignment,
  updateProsecutorAssignment,
} from "services/prosecutorAssignentService";
import { LEGAL_RESEARCH_REQUEST_STATUS } from "utils/constantValues";
import { getInitials } from "utils/helpers";
import {
  Users,
  UserPlus,
  UserCog,
  Calendar,
  MessageSquare,
  Award,
  X,
  ChevronDown,
  Shield,
  CheckCircle,
  AlertCircle,
  Building2,
} from "lucide-react";

interface TeamMember {
  id: string;
  name: string;
  role: string;
  isTeamLeader: boolean;
  prosecutor_name?: string;
  is_team_leader?: boolean;
  department?: string;
}

interface EmployeeData {
  user: {
    id: number;
    first_name: string;
    last_name?: string;
  };
  roles: string[];
  department?: string;
}

interface SettingUpTeamModalProps {
  request: any;
  isOpen: boolean;
  onClose: () => void;
  setLegalResearch: Function;
  onAssignProsecutor: (data: {
    remark: string;
    proposalDeadline: string;
    teamMembers: TeamMember[];
  }) => void;
}

const SettingUpTeamModal: React.FC<SettingUpTeamModalProps> = ({
  request,
  setLegalResearch,
  isOpen,
  onClose,
  onAssignProsecutor,
}) => {
  const { t } = useTranslation();
  const [remark, setRemark] = useState(request.remark_for_team || "");
  const [proposalDeadline, setProposalDeadline] = useState(
    new Date().toISOString().split("T")[0],
  );
  const [selectedProsecutor, setSelectedProsecutor] =
    useState<TeamMember | null>(null);
  const [employees, setEmployees] = useState<TeamMember[]>([]);
  const contextData = useAuth();
  const { showToast } = useToast();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>(
    (request.prosecutor_assignments ?? []).map((member: any) => ({
      ...member,
      isTeamLeader: member.isTeamLeader ?? member.is_team_leader ?? false,
      id: member.prosecutor_user_id,
      name: member.prosecutor_name,
      role: "Prosecutor",
      department: member.department || t("LegalDepartment"),
    })),
  );
  const [submitting, setSubmitting] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      getEmployeesByAdminAndOrg({
        bearer:
          contextData.token ??
          contextData?.user?.access_token ??
          contextData?.user.user?.access_token,
        org_unit: contextData?.user?.org_unit || "",
        admin_unit: contextData?.user?.admin_unit || "",
      })
        .then((data: EmployeeData[]) => {
          const prosecutors = data
            .filter((emp) =>
              emp.roles?.some(
                (role: string) => role === "LegalResearchProsecutor",
              ),
            )
            .map((emp) => ({
              id: emp.user.id.toString(),
              name: `${emp.user.first_name} ${emp.user.last_name || ""}`.trim(),
              role: "Legal Research Prosecutor",
              isTeamLeader: false,
              department: emp.department || t("LegalDepartment"),
            }));
          setEmployees(prosecutors);
        })
        .catch((error) => {
          console.error("Error fetching:", error);
          showToast(t("FailedToFetchEmployees"), "error");
        });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        modalContentRef.current &&
        !modalContentRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const currentTeamLeaders = teamMembers.filter(
      (member) => member.isTeamLeader,
    );
    const errors = [];

    if (teamMembers.length === 0) {
      errors.push(t("PleaseAddAtLeastOneTeamMember"));
    }
    if (currentTeamLeaders.length !== 1) {
      errors.push(t("PleaseSelectExactlyOneTeamLeader"));
    }
    if (!proposalDeadline) {
      errors.push(t("PleaseSetProposalDeadline"));
    }

    if (errors.length > 0) {
      setValidationError(errors.join(" "));
      return;
    }

    setValidationError(null);
    setSubmitting(true);

    try {
      // Remove deleted prosecutors
      const currentIds = teamMembers.map((m) => m.id.toString());
      if (request.prosecutor_assignments) {
        for (const prosecutor of request.prosecutor_assignments) {
          if (!currentIds.includes(prosecutor.prosecutor_user_id)) {
            await deleteProsecutorAssignment(prosecutor.id);
          }
        }
      }

      // Update or create prosecutors
      for (const member of teamMembers) {
        const prosecutorData = new FormData();

        if (request.requested_by === "internal") {
          prosecutorData.append(
            "internal_research_request",
            request.id.toString(),
          );
          prosecutorData.append("team_type", "prosecutor_research_team");
        } else if (request.request_summary) {
          prosecutorData.append(
            "research_committe_report",
            request.id.toString(),
          );
          prosecutorData.append("team_type", "prosecutor_research_team");
        } else {
          prosecutorData.append("research_request", request.id.toString());
        }

        prosecutorData.append("prosecutor_name", member.name);
        prosecutorData.append("prosecutor_user_id", member.id.toString());
        prosecutorData.append(
          "is_team_leader",
          member.isTeamLeader ? "true" : "false",
        );

        const currentMember = request.prosecutor_assignments?.find(
          (prosecutor: any) =>
            prosecutor.prosecutor_user_id === member.id.toString(),
        );

        if (currentMember) {
          await updateProsecutorAssignment(currentMember.id, prosecutorData);
        } else {
          await createProsecutorAssignment(prosecutorData);
        }
      }

      // Update request metadata
      const data = new FormData();
      data.append("remark_for_team", remark);
      data.append("proposal_deadline", proposalDeadline);
      data.append(
        "forward_status",
        LEGAL_RESEARCH_REQUEST_STATUS.assigned_to_prosecutor.value,
      );
      data.append(
        "status",
        LEGAL_RESEARCH_REQUEST_STATUS.proposal_underway.value,
      );

      let response: any;
      if (request.requested_by === "internal") {
        response = await updateInternalLegalResearchRequest(
          contextData.token,
          request.id,
          data,
        );
      } else if (request.request_summary) {
        response = await updateLegalResearchRequestCommittee(
          request.id,
          data,
          contextData.token,
        );
      } else {
        response = await updateLegalResearchRequest(
          request.id,
          data,
          contextData.token,
        );
      }

      setLegalResearch((prev: any) => ({
        ...prev,
        ...response,
      }));

      showToast(t("TeamAssignedSuccess"), "success");
      onAssignProsecutor({ remark, proposalDeadline, teamMembers });
      onClose();
    } catch (error: any) {
      console.error("Error submitting request:", error);
      showToast(
        error.response?.data?.message || t("FailedToAssignTeam"),
        "error",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = (id: string) => {
    const memberToRemove = teamMembers.find((member) => member.id === id);
    if (
      memberToRemove?.isTeamLeader &&
      teamMembers.filter((m) => m.isTeamLeader).length === 1
    ) {
      setValidationError(t("CannotRemoveOnlyTeamLeader"));
      return;
    }
    setTeamMembers(teamMembers.filter((member) => member.id !== id));
    setValidationError(null);
  };

  const handleToggleTeamLeader = (id: string) => {
    setTeamMembers((prevMembers) =>
      prevMembers.map((member) => ({
        ...member,
        isTeamLeader: member.id === id,
      })),
    );
    setValidationError(null);
  };

  const handleAddProsecutor = () => {
    if (!selectedProsecutor) {
      setValidationError(t("PleaseSelectProsecutorToAdd"));
      return;
    }

    if (teamMembers.some((member) => member.id === selectedProsecutor.id)) {
      setValidationError(t("ProsecutorAlreadyInTeam"));
      return;
    }

    setTeamMembers((prevMembers) => [
      ...prevMembers,
      {
        ...selectedProsecutor,
        isTeamLeader: prevMembers.length === 0,
      },
    ]);
    setSelectedProsecutor(null);
    setValidationError(null);
  };

  const getInitialsWithFallback = (name: string) => {
    try {
      return getInitials(name);
    } catch {
      return name.substring(0, 2).toUpperCase();
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900/70 backdrop-blur-sm flex items-center justify-end p-4 z-[100]">
      <div
        ref={modalContentRef}
        className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col h-[90vh] border border-gray-300"
      >
        {/* Header */}
        <div className="bg-white px-8 py-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-yellow-100 rounded-lg">
                <Users className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {t("AssembleResearchTeam")}
                </h2>
                <p className="text-gray-600 text-sm mt-1">
                  {request.request_summary
                    ? t("CommitteeReviewTeamAssignment")
                    : t("SelectAndAssignProsecutors")}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-600 hover:text-gray-900 transition-colors p-2 hover:bg-gray-100 rounded-lg"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col">
          <form
            onSubmit={handleSubmit}
            className="flex-1 flex flex-col overflow-hidden"
          >
            <div className="flex-1 overflow-y-auto p-8">
              {/* Team Composition Section */}
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4">
                  <UserCog className="w-5 h-5 text-gray-700" />
                  <h3 className="text-lg font-semibold text-gray-900">
                    {t("TeamComposition")}
                  </h3>
                </div>

                {/* Prosecutor Selection */}
                <div className="mb-6 p-4 bg-white border border-gray-300 rounded-lg">
                  <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                    <UserPlus className="w-4 h-4 text-gray-600" />
                    {t("AddProsecutorToTeam")}
                  </label>
                  <div className="flex gap-3 mb-4">
                    <div className="flex-1 relative">
                      <select
                        value={selectedProsecutor?.id || ""}
                        onChange={(e) =>
                          setSelectedProsecutor(
                            employees.find(
                              (employee) =>
                                employee.id.toString() === e.target.value,
                            ) || null,
                          )
                        }
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent appearance-none text-gray-900"
                      >
                        <option value="">{t("SelectProsecutor")}</option>
                        {employees.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.name} - {emp.department}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-3.5 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                    <button
                      type="button"
                      onClick={handleAddProsecutor}
                      className="px-6 py-3 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium flex items-center gap-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      {t("Add")}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500">
                    {t("SelectFromAvailableProsecutors")}
                  </p>
                </div>

                {/* Team Members List */}
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-medium text-gray-900">
                      {t("TeamMembersCount", { count: teamMembers.length })}
                    </h4>
                    {teamMembers.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-[#8B0000]"></div>
                          <span>{t("TeamLeader")}</span>
                        </div>
                        <div className="flex items-center gap-1 ml-3">
                          <div className="w-2 h-2 rounded-full bg-gray-400"></div>
                          <span>{t("Member")}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
                    {teamMembers.length === 0 ? (
                      <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="text-gray-600">
                          {t("NoTeamMembersAddedYet")}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {t("AddProsecutorsToAssembleTeam")}
                        </p>
                      </div>
                    ) : (
                      teamMembers.map((member) => (
                        <div
                          key={member.id}
                          className={`flex items-center p-4 rounded-lg border transition-all ${
                            member.isTeamLeader
                              ? "border-[#8B0000] bg-[#8B0000]/5"
                              : "border-gray-200 bg-gray-50"
                          }`}
                        >
                          <div className="flex items-center gap-4 flex-1">
                            <div
                              className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-white ${
                                member.isTeamLeader
                                  ? "bg-[#8B0000]"
                                  : "bg-gray-700"
                              }`}
                            >
                              {getInitialsWithFallback(member.name)}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-gray-900">
                                  {member.name}
                                </h4>
                                {member.isTeamLeader && (
                                  <span className="px-2 py-1 bg-[#8B0000] text-white text-xs font-medium rounded-full flex items-center gap-1">
                                    <Award className="w-3 h-3" />
                                    {t("TeamLeader")}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-gray-600">
                                  {member.role}
                                </span>
                                <span className="text-gray-400">•</span>
                                <span className="text-gray-500">
                                  {member.department}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <button
                              type="button"
                              onClick={() => handleToggleTeamLeader(member.id)}
                              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                                member.isTeamLeader
                                  ? "bg-[#8B0000]/10 text-[#8B0000] hover:bg-[#8B0000]/20"
                                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                              }`}
                            >
                              {member.isTeamLeader ? t("LeaderCheck") : t("MakeLeader")}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveMember(member.id)}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* Deadline & Remarks Section */}
              {!request.request_summary && (
                <div className="space-y-6">
                  <div className="p-4 bg-white border border-gray-300 rounded-lg">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                      <Calendar className="w-4 h-4 text-gray-600" />
                      {t("ProposalDeadline")}
                    </label>
                    <div className="relative">
                      <input
                        type="date"
                        value={proposalDeadline}
                        onChange={(e) => setProposalDeadline(e.target.value)}
                        min={new Date().toISOString().split("T")[0]}
                        required
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent text-gray-900"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      {t("SetProposalDeadlineDescription")}
                    </p>
                  </div>

                  <div className="p-4 bg-white border border-gray-300 rounded-lg">
                    <label className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                      <MessageSquare className="w-4 h-4 text-gray-600" />
                      {t("TeamInstructionsAndRemarks")}
                    </label>
                    <textarea
                      value={remark}
                      onChange={(e) => setRemark(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-none placeholder-gray-500 text-gray-900"
                      placeholder={t("TeamInstructionsPlaceholder")}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      {t("TeamInstructionsOptional")}
                    </p>
                  </div>
                </div>
              )}

              {/* Validation Error */}
              {validationError && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-800">{validationError}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer Actions */}
            <div className="border-t border-gray-300 px-8 py-6 bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    <span>{t("ConfidentialTeamAssignment")}</span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-6 py-3 border border-gray-900 text-gray-900 font-semibold rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-gray-800 focus:ring-offset-2"
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-3 bg-gradient-to-r from-gray-900 to-gray-800 text-white font-semibold rounded-lg hover:from-gray-800 hover:to-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all focus:outline-none focus:ring-2 focus:ring-gray-800 focus:ring-offset-2 flex items-center gap-2"
                  >
                    {submitting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {t("AssigningTeam")}
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        {t("ConfirmTeamAssignment")}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SettingUpTeamModal;